import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchManagedHotels, approveHotel } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import AccountSidebar from "@/components/AccountSidebar";
import { MapPin, Hotel as HotelIcon } from "lucide-react";
import { fmtPrice, withWatermark } from "@/lib/utils";

export default function AdminHotelsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchManagedHotels()
      .then(setItems)
      .catch((e) => setError(e?.response?.status === 403 ? "Acces reserve" : "Erreur de chargement"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-hotels-page">
      <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Gestion des hotels</h1>
      <p className="text-[var(--ink-soft)] mt-2">Selectionnez un hotel pour gerer ses disponibilites et ses tarifs.</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <AccountSidebar />
        <section>
          {loading ? (
            <div className="glass-card p-10 text-center">Chargement...</div>
          ) : error ? (
            <div className="glass-card p-10 text-center text-red-600 font-semibold" data-testid="admin-hotels-error">{error}</div>
          ) : items.length === 0 ? (
            <div className="glass-card p-12 text-center" data-testid="admin-hotels-empty">
              <HotelIcon size={36} className="mx-auto text-orange-500 mb-3" />
              <div className="font-bold text-lg mb-2">Aucun hotel a gerer</div>
              <p className="text-sm text-[var(--ink-soft)]">Contactez l'administration Fodindin pour devenir hotel partenaire.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((h) => (
                <Link
                  key={h.id}
                  to={`/admin/hotels/${h.id}/disponibilites`}
                  className="card"
                  data-testid={`admin-hotel-${h.id}`}
                >
                  <img src={withWatermark(h.image)} className="w-full h-40 object-cover" alt={h.name} />
                  <div className="p-4">
                    <div className="font-bold">{h.name}</div>
                    <div className="text-xs text-[var(--ink-soft)] mt-1 flex items-center gap-1">
                      <MapPin size={12} /> {h.city}{h.quartier ? ` - ${h.quartier}` : ""}
                    </div>
                    <div className="mt-2 text-orange-600 font-bold text-sm">{fmtPrice(h.price)} / nuit</div>
                    {h.offre_lancement && <div className="mt-1 text-[11px] font-semibold text-emerald-700" data-testid={`admin-hotel-lancement-${h.id}`}>Offre de lancement n°{h.offre_lancement.place_no}</div>}
                    {h.pending_review && (
                      <div className="mt-2 flex items-center justify-between gap-2 text-[11px] font-bold text-amber-700 bg-amber-50 rounded-lg px-2 py-1">
                        En attente de validation
                        {user?.is_admin && <button type="button" onClick={(e) => { e.preventDefault(); approveHotel(h.id).then(() => { toast.success("Hotel publie"); setItems((l) => l.map((x) => (x.id === h.id ? { ...x, pending_review: false } : x))); }).catch(() => toast.error("Echec")); }} className="underline" data-testid={`admin-hotel-approve-${h.id}`}>Publier</button>}
                      </div>
                    )}
                    <div className="mt-3 btn-secondary w-full text-center !text-xs">Gerer les disponibilites</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
